/**
 * Launcher-message ranking (owner ruling 2026-10-26). One message shows over the launcher and the
 * launcher opens the same slot's thread, so both read the SAME ranking:
 *   specificity of the variant that MATCHED (path > origin > visitors > none), then the operator's
 *   slot order. Only MESSAGE slots take part (HINT / SYSTEM live inside a thread).
 */
import type { ContentRule, MessageSlot, MessageVariant } from "../widget-config";
import { type MatchContext, pickVariant } from "./match";

/** Higher = more specific. */
export const SPECIFICITY = { none: 0, visitors: 1, origin: 2, path: 3 } as const;

export interface RankedSlot {
  slotId: string;
  variant: MessageVariant;
  specificity: number;
  /** Position in the operator's slot list (the tie-break). */
  order: number;
  /** Days an X dismiss lasts; `null` = until the content changes. */
  reappearAfterDays: number | null;
}

const hasItems = (v: unknown): boolean => Array.isArray(v) && v.length > 0;

/** The most specific condition a variant's rule carries. */
export function variantSpecificity(when: ContentRule | undefined): number {
  if (!when) return SPECIFICITY.none;
  if (hasItems(when.paths)) return SPECIFICITY.path;
  if (hasItems(when.origins)) return SPECIFICITY.origin;
  if (when.visitors === "ANONYMOUS" || when.visitors === "SIGNED_IN") return SPECIFICITY.visitors;
  return SPECIFICITY.none;
}

/** Every MESSAGE slot with a matching variant, most important first. */
export function rankMessageSlots(slots: readonly MessageSlot[], ctx: MatchContext): RankedSlot[] {
  const out: RankedSlot[] = [];
  slots.forEach((slot, order) => {
    if (slot.kind !== "MESSAGE") return;
    const variant = pickVariant(slot.variants, ctx);
    if (!variant) return;
    out.push({
      slotId: slot.id,
      variant,
      specificity: variantSpecificity(variant.when),
      order,
      reappearAfterDays: slot.reappearAfterDays ?? null,
    });
  });
  return out.sort((a, b) => b.specificity - a.specificity || a.order - b.order);
}

/** The slot the page decides on: the bubble and the launcher both point here. */
export function topMessageSlot(
  slots: readonly MessageSlot[],
  ctx: MatchContext,
): RankedSlot | null {
  return rankMessageSlots(slots, ctx)[0] ?? null;
}
